/**
 * Identified Visitors — passively identity-matched contacts (via our
 * identity-match data provider). Kept separate from Customer Leads, which
 * are reserved for people who gave us their info directly. Once a contact
 * replies, mark them Replied — they'll graduate into Customer Leads.
 */
import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";
import { UserCheck, Mail, MapPin, Calendar } from "lucide-react";

const STATUS_STYLES = {
  New: "bg-blue-50 text-blue-700 border-blue-200",
  Emailed: "bg-amber-50 text-amber-700 border-amber-200",
  Replied: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

const StatusSelect = ({ visitor, onChange, disabled }) => (
  <select
    value={visitor.status}
    disabled={disabled}
    onChange={(e) => onChange(visitor.id, e.target.value)}
    className={`text-[12px] font-semibold px-2.5 py-1 rounded-full border cursor-pointer ${STATUS_STYLES[visitor.status] || STATUS_STYLES.New}`}
    data-testid={`identified-visitor-status-${visitor.id}`}
  >
    <option value="New">New</option>
    <option value="Emailed">Emailed</option>
    <option value="Replied">Replied</option>
  </select>
);

const IdentifiedVisitorsPage = () => {
  const { apiFetch } = useAuth();
  const { success: toastOk, error: toastErr } = useToast();
  const [visitors, setVisitors] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    apiFetch("/identified-visitors")
      .then((res) => {
        setVisitors(res.data.visitors || []);
        setTotal(res.data.total || 0);
      })
      .catch(() => toastErr("Could not load identified visitors"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiFetch]);

  useEffect(() => { load(); }, [load]);

  const changeStatus = async (id, status) => {
    setUpdatingId(id);
    try {
      const res = await apiFetch(`/identified-visitors/${id}`, { method: "PATCH", data: { status } });
      setVisitors((prev) => prev.map((v) => (v.id === id ? { ...v, status } : v)));
      if (res.data?.graduated_lead_id) {
        toastOk("Marked Replied — added to Customer Leads");
      } else {
        toastOk(`Status updated to ${status}`);
      }
    } catch (err) {
      toastErr(err.response?.data?.detail || "Failed to update status");
    } finally {
      setUpdatingId(null);
    }
  };

  const mailingAddress = (v) => {
    const parts = [v.mailing_street, v.mailing_city, [v.mailing_state, v.mailing_zip].filter(Boolean).join(" ")]
      .filter(Boolean);
    return parts.length ? parts.join(", ") : "—";
  };

  return (
    <div className="p-6 w-full" data-testid="identified-visitors-page">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-1">
          <div className="p-2 bg-indigo-100 rounded-lg">
            <UserCheck className="w-5 h-5 text-indigo-600" />
          </div>
          <h1 className="text-[20px] font-bold text-gray-900">Identified Visitors</h1>
        </div>
        <p className="text-[13px] text-gray-500 ml-11">
          Passively matched contacts — separate from Customer Leads until they reply.
        </p>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="py-16 text-center text-gray-400 text-sm" data-testid="identified-visitors-loading">
            Loading…
          </div>
        ) : visitors.length === 0 ? (
          <div className="py-16 text-center" data-testid="identified-visitors-empty-state">
            <UserCheck className="w-8 h-8 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-500 text-sm">No identified visitors yet</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Name</th>
                <th className="text-left px-4 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Email</th>
                <th className="text-left px-4 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Mailing Address</th>
                <th className="text-left px-4 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Date Matched</th>
                <th className="text-left px-4 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="text-left px-4 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Source</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {visitors.map((v) => (
                <tr key={v.id} data-testid={`identified-visitor-row-${v.id}`}>
                  <td className="px-4 py-3 font-medium text-gray-900">{v.name || "—"}</td>
                  <td className="px-4 py-3 text-gray-600">
                    <span className="inline-flex items-center gap-1.5"><Mail size={13} className="text-gray-400" />{v.email || "—"}</span>
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    <span className="inline-flex items-center gap-1.5"><MapPin size={13} className="text-gray-400" />{mailingAddress(v)}</span>
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    <span className="inline-flex items-center gap-1.5">
                      <Calendar size={13} className="text-gray-400" />
                      {v.date_matched ? new Date(v.date_matched).toLocaleDateString() : "—"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <StatusSelect visitor={v} onChange={changeStatus} disabled={updatingId === v.id} />
                  </td>
                  <td className="px-4 py-3 text-gray-500">{v.source}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {total > 0 && (
        <p className="text-[12px] text-gray-400 mt-3" data-testid="identified-visitors-total">{total} total</p>
      )}
    </div>
  );
};

export default IdentifiedVisitorsPage;
